/**
 * Splits a single "First Last Extra" display name into firstName (first
 * word) and lastName (everything else).
 */
export function splitName(fullName) {
  const [first, ...rest] = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
  return { firstName: first ?? "", lastName: rest.join(" ") };
}

/** A pet is considered complete once it has a name, gender, and species. */
export function petHasMissingValues(pet) {
  return !pet?.name || !pet?.gender || !pet?.species;
}

/** A user is considered complete once they have a first name, last name, and email. */
export function userHasMissingValues(user) {
  const { firstName, lastName } =
    user?.firstName || user?.lastName
      ? { firstName: user?.firstName, lastName: user?.lastName }
      : splitName(user?.name);

  return !firstName || !lastName || !user?.email;
}

export function userHasAnyUpdate(user) {
  return Boolean(user?.updated) || Boolean(user?.pets?.some((pet) => pet.updated));
}

/**
 * Overall row status shown in the import table:
 *  - "conflict": the email or phone number is already used by an account or by another
 *    row of this import (see useImportConflicts). Blocks the import, so it comes first.
 *  - "missing": the user or at least one of their pets is missing a
 *    required field. Takes priority over "updated" - a row can still be
 *    incomplete even if someone already touched it.
 *  - "updated": nothing missing, but the user or a pet was edited.
 *  - "ready": nothing missing, nothing edited - importable as-is.
 */
export function getUserImportStatus(user, conflict = null) {
  if (conflict) return "conflict";
  const missing =
    userHasMissingValues(user) || Boolean(user?.pets?.some(petHasMissingValues));

  if (missing) return "missing";
  if (!missing && userHasAnyUpdate(user)) return "ready";
  if (userHasAnyUpdate(user)) return "updated";
  return "ready";
}
